import React, { useState, useCallback } from 'react';
import { Outlet } from 'react-router-dom';
import { AlertTriangle, Mail, X, RefreshCw } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../hooks/useAuth';
import { useToast } from '../hooks/useToast';

export interface RequireEmailVerificationProps {
  /** Optional children to render instead of Outlet */
  children?: React.ReactNode;
}

/**
 * RequireEmailVerification
 *
 * Wrapper component for protected routes that displays a persistent
 * email verification banner when the authenticated user has not yet
 * verified their email address. Does NOT block access — users can
 * still use the dashboard but see a dismissible reminder banner.
 *
 * @example
 * ```tsx
 * <Route element={<ProtectedRoute />}>
 *   <Route element={<RequireEmailVerification />}>
 *     <Route path="dashboard" element={<DashboardPage />} />
 *   </Route>
 * </Route>
 * ```
 */
export const RequireEmailVerification: React.FC<RequireEmailVerificationProps> = ({
  children,
}) => {
  const { user } = useAuth();
  const toast = useToast();
  const [isDismissed, setIsDismissed] = useState(false);
  const [isResending, setIsResending] = useState(false);

  const showBanner = user && !user.emailVerified && !isDismissed;

  /**
   * Handle resending verification email.
   * Uses dynamic import to avoid circular dependencies.
   */
  const handleResendVerification = useCallback(async () => {
    if (isResending || !user) return;

    setIsResending(true);
    try {
      const { authService } = await import('../services/authService');
      await authService.resendVerificationEmail(user.email);
      toast.success(
        'Verification email sent!',
        'Please check your inbox and spam folder.'
      );
    } catch {
      toast.error(
        'Failed to send email',
        'Please try again in a few moments.'
      );
    } finally {
      setIsResending(false);
    }
  }, [isResending, user, toast]);

  const handleDismiss = useCallback(() => {
    setIsDismissed(true);
  }, []);

  return (
    <>
      {/* Email Verification Banner */}
      <AnimatePresence>
        {showBanner && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: [0.25, 0.1, 0.25, 1] }}
            className="overflow-hidden"
          >
            <div className="relative bg-warning-50 dark:bg-warning-950/30 border-b border-warning-200 dark:border-warning-800">
              <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                <div className="flex items-center justify-between gap-4 py-3">
                  {/* Icon + Message */}
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="flex-shrink-0">
                      <AlertTriangle className="w-5 h-5 text-warning-500" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-warning-800 dark:text-warning-200">
                        Please verify your email address
                      </p>
                      <p className="text-xs text-warning-600 dark:text-warning-400 mt-0.5 hidden sm:block">
                        We sent a verification link to{' '}
                        <span className="font-medium">{user?.email}</span>.
                        Check your inbox and spam folder.
                      </p>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-2 flex-shrink-0">
                    {/* Resend button */}
                    <button
                      type="button"
                      onClick={handleResendVerification}
                      disabled={isResending}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md bg-warning-100 dark:bg-warning-900/50 text-warning-700 dark:text-warning-300 hover:bg-warning-200 dark:hover:bg-warning-900 transition-colors duration-150 disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-warning-500/30 focus:ring-offset-1"
                    >
                      {isResending ? (
                        <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                      ) : (
                        <Mail className="w-3.5 h-3.5" />
                      )}
                      <span className="hidden xs:inline">
                        {isResending ? 'Sending...' : 'Resend email'}
                      </span>
                    </button>

                    {/* Dismiss button */}
                    <button
                      type="button"
                      onClick={handleDismiss}
                      className="inline-flex items-center justify-center w-7 h-7 rounded-md text-warning-500 hover:text-warning-700 dark:hover:text-warning-300 hover:bg-warning-100 dark:hover:bg-warning-900/50 transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-warning-500/30 focus:ring-offset-1"
                      aria-label="Dismiss verification banner"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Route content */}
      {children ? <>{children}</> : <Outlet />}
    </>
  );
};

RequireEmailVerification.displayName = 'RequireEmailVerification';